// src/pages/rh/AlertesContrats.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Card, Row, Col, Button, Alert, Badge, Table, Spinner, Pagination } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";

// Composant de pagination réutilisable
const CustomPagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <Pagination className="mb-0">
            <Pagination.Prev 
                onClick={() => onPageChange(currentPage - 1)} 
                disabled={currentPage === 1}
            />
            {getPageNumbers().map((page, index) => (
                page === '...' ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                ) : (
                    <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => onPageChange(page)}
                    >
                        {page}
                    </Pagination.Item>
                )
            ))}
            <Pagination.Next 
                onClick={() => onPageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
            />
        </Pagination>
    );
};

// Composant de tableau avec pagination pour chaque section
const AlertTable = ({ title, icon, iconColor, data, columns, onView, variant, badgeColor }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    
    // Réinitialiser la page quand les données changent
    useEffect(() => {
        setCurrentPage(1);
    }, [data.length]);

    const totalPages = Math.ceil(data.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentData = data.slice(startIndex, startIndex + itemsPerPage);

    if (data.length === 0) return null;

    return (
        <Col lg={12}>
            <Card className={`border-0 shadow-sm rounded-3 ${variant === 'warning' ? 'border-warning' : variant === 'danger' ? 'border-danger' : ''}`}>
                <Card.Header className={`bg-transparent border-0 pt-4 ${variant === 'warning' ? 'border-warning' : variant === 'danger' ? 'border-danger' : ''}`}>
                    <h6 className={`mb-0 fw-semibold ${variant === 'warning' ? 'text-warning' : variant === 'danger' ? 'text-danger' : ''}`}>
                        <i className={`${icon} me-2`} style={{ color: iconColor }}></i>
                        {title} ({data.length})
                    </h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    {columns.map((col, idx) => (
                                        <th key={idx} className={col.className || ''}>{col.label}</th>
                                    ))}
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentData.map((item, idx) => (
                                    <tr key={item.id} className={variant === 'warning' ? 'table-warning' : ''}>
                                        {columns.map((col, colIdx) => (
                                            <td key={colIdx} className={col.className || ''}>
                                                {col.render ? col.render(item) : item[col.field]}
                                            </td>
                                        ))}
                                        <td className="text-center pe-4">
                                            <Button 
                                                size="sm" 
                                                variant={badgeColor || "outline-primary"}
                                                onClick={() => onView(item.id)}
                                                className="rounded-pill"
                                            >
                                                <i className="ri-eye-line me-1"></i>
                                                Voir
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center p-3 border-top">
                            <div className="text-muted small">
                                Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, data.length)} sur {data.length}
                            </div>
                            <CustomPagination 
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );
};

export default function AlertesContrats() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [alerts, setAlerts] = useState({
        debut_proche: [],
        fin_proche: [],
        expires: [],
        encours: []
    });
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchAlerts();
        fetchStats();
    }, []);

    const fetchAlerts = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?action=alerts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement alertes");
            
            const result = await response.json();
            setAlerts(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    // Colonnes pour le tableau des contrats qui commencent
    const debutProcheColumns = [
        { label: 'Employé', field: 'employe_nom', className: 'ps-4', render: (item) => (
            <div className="ps-4">
                <div className="fw-semibold">{item.employe_prenom} {item.employe_nom}</div>
                <small className="text-muted">{item.employe_email}</small>
            </div>
        )},
        { label: 'Employeur', render: (item) => (
            <div>
                <div>{item.employeur_nom}</div>
                <small className="text-muted">{item.employeur_email}</small>
            </div>
        )},
        { label: 'Date de début', render: (item) => (
            <Badge bg="warning" className="rounded-pill text-white">
                <i className="ri-calendar-line me-1"></i>
                {formatDate(item.date_debut)}
            </Badge>
        )},
        { label: 'Statut', render: () => <Badge bg="warning" className="rounded-pill text-white" style={{color:'white'}}>À venir</Badge> }
    ];

    // Colonnes pour le tableau des contrats qui expirent
    const finProcheColumns = [
        { label: 'Employé', className: 'ps-4', render: (item) => (
            <div className="ps-4">
                <div className="fw-semibold">{item.employe_prenom} {item.employe_nom}</div>
                <small className="text-muted">{item.employe_email}</small>
            </div>
        )},
        { label: 'Employeur', render: (item) => (
            <div>
                <div>{item.employeur_nom}</div>
                <small className="text-muted">{item.employeur_email}</small>
            </div>
        )},
        { label: 'Date de fin', render: (item) => (
            <Badge bg="danger" className="rounded-pill">
                <i className="ri-calendar-close-line me-1"></i>
                {formatDate(item.date_fin)}
            </Badge>
        )},
        { label: 'Jours restants', render: (item) => {
            const dateFin = new Date(item.date_fin);
            const today = new Date();
            const joursRestants = Math.ceil((dateFin - today) / (1000 * 60 * 60 * 24));
            return <Badge bg="warning" className="rounded-pill text-white">{joursRestants} jour(s)</Badge>;
        }}
    ];

    // Colonnes pour le tableau des contrats expirés
    const expiresColumns = [
        { label: 'Employé', className: 'ps-4', render: (item) => (
            <div className="ps-4">
                <div className="fw-semibold">{item.employe_prenom} {item.employe_nom}</div>
                <small className="text-muted">{item.employe_email}</small>
            </div>
        )},
        { label: 'Employeur', render: (item) => (
            <div>
                <div>{item.employeur_nom}</div>
                <small className="text-muted">{item.employeur_email}</small>
            </div>
        )},
        { label: "Date d'expiration", render: (item) => (
            <Badge bg="danger" className="rounded-pill">
                {formatDate(item.date_fin)}
            </Badge>
        )},
        { label: 'Statut', render: () => <Badge bg="danger" className="rounded-pill">Expiré</Badge> }
    ];

    // Colonnes pour le tableau des contrats en cours
    // const encoursColumns = [
    //     { label: 'Employé', className: 'ps-4', render: (item) => (
    //         <div className="ps-4">
    //             <div className="fw-semibold">{item.employe_prenom} {item.employe_nom}</div>
    //             <small className="text-muted">{item.employe_email}</small>
    //         </div>
    //     )},
    //     { label: 'Employeur', render: (item) => (
    //         <div>
    //             <div>{item.employeur_nom}</div>
    //             <small className="text-muted">{item.employeur_email}</small>
    //         </div>
    //     )},
    //     { label: 'Date de début', render: (item) => formatDate(item.date_debut) },
    //     { label: 'Date de fin', render: (item) => item.date_fin ? formatDate(item.date_fin) : 'CDI' },
    //     { label: 'Statut', render: () => <Badge bg="success" className="rounded-pill">Actif</Badge> }
    // ];

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des alertes...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-warning bg-opacity-10 rounded-3">
                                    <i className="ri-alert-line fs-20 text-warning"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Alertes contrats</h4>
                                    <p className="text-muted small mb-0">Suivi des échéances et alertes</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={fetchAlerts}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-refresh-line"></i>
                                Actualiser
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => navigate('/rh/contrats')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour aux contrats
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon"><i className="ri-file-copy-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Total contrats</span>
                                        <h3 className="kpi-value">{stats.total || 0}</h3>
                                        <span className="kpi-unit">Contrats enregistrés</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon"><i className="ri-checkbox-circle-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Contrats actifs</span>
                                        <h3 className="kpi-value text-success">{stats.actifs || 0}</h3>
                                        <span className="kpi-unit">En cours de validité</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon"><i className="ri-time-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">CDI</span>
                                        <h3 className="kpi-value text-warning">{stats.cdi || 0}</h3>
                                        <span className="kpi-unit">Sans date de fin</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon"><i className="ri-error-warning-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Expirés</span>
                                        <h3 className="kpi-value text-danger">{stats.expires || 0}</h3>
                                        <span className="kpi-unit">Contrats terminés</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Alertes avec pagination */}
                    <Row className="g-4">
                        {/* Contrats qui commencent bientôt */}
                        <AlertTable
                            title="Contrats qui commencent bientôt"
                            icon="ri-calendar-line"
                            iconColor="#f59e0b"
                            data={alerts.debut_proche || []}
                            columns={debutProcheColumns}
                            onView={(id) => navigate(`/rh/contrats/${id}`)}
                            variant="warning"
                            badgeColor="warning"
                        />

                        {/* Contrats qui finissent bientôt */}
                        <AlertTable
                            title="Contrats qui expirent bientôt"
                            icon="ri-alert-line"
                            iconColor="#dc3545"
                            data={alerts.fin_proche || []}
                            columns={finProcheColumns}
                            onView={(id) => navigate(`/rh/contrats/${id}`)}
                            variant="warning"
                            badgeColor="warning"
                        />

                        {/* Contrats expirés */}
                        <AlertTable
                            title="Contrats expirés"
                            icon="ri-error-warning-line"
                            iconColor="#dc3545"
                            data={alerts.expires || []}
                            columns={expiresColumns}
                            onView={(id) => navigate(`/rh/contrats/${id}`)}
                            variant="danger"
                            badgeColor="danger"
                        />

                        {/* Contrats en cours */}
                        {/* <AlertTable
                            title="Contrats en cours"
                            icon="ri-checkbox-circle-line"
                            iconColor="#10b981"
                            data={alerts.encours || []}
                            columns={encoursColumns}
                            onView={(id) => navigate(`/rh/contrats/${id}`)}
                            variant="success"
                            badgeColor="success"
                        /> */}

                        {/* Aucune alerte */}
                        {alerts.debut_proche?.length === 0 && 
                         alerts.fin_proche?.length === 0 && 
                         alerts.expires?.length === 0 && 
                         alerts.encours?.length === 0 && (
                            <Col lg={12}>
                                <Card className="border-0 shadow-sm rounded-3 text-center">
                                    <Card.Body className="py-5">
                                        <i className="ri-checkbox-circle-line text-success fs-48 mb-3 d-block"></i>
                                        <h5 className="mb-2">Aucune alerte</h5>
                                        <p className="text-muted mb-0">Tous les contrats sont à jour</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .kpi-card { background: white; border-radius: 0px; padding: 1rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: all 0.2s ease; height: 100%; border-left: 2px solid; }
                .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: #21ACCD; } .kpi-success { border-left-color: #10b981; } .kpi-danger { border-left-color: #ef4444; } .kpi-warning { border-left-color: #f59e0b; }
                .kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-content { flex: 1; }
                .kpi-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value { font-size: 1.3rem; font-weight: 700; margin: 0.25rem 0; color: #1a3c5e; }
                .kpi-unit { font-size: 0.7rem; color: #6c757d; }
                .fs-20 { font-size: 1.25rem; }
                .fs-24 { font-size: 1.5rem; }
                .fs-48 { font-size: 3rem; }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
                .table-warning { background-color: #fff3cd !important; }
            `}</style>
        </>
    );
}